import { useTranslation } from '../presentation/common/useTranslation.js';
import { presentationText } from '../presentation/text';
import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';

import { Users, Shield, Sliders, LogOut, Code, Calendar, Gavel, User, Sword, Menu, X, Store, LayoutGrid, Scale, Award, LayoutDashboard, ShieldAlert } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { logoutUser } from '../services/api/auth';
import { getAdminBadges } from '../services/api/admin';
import '../styles/world.css';

import ThemeSwitcher from './ThemeSwitcher';
import LanguageSwitcher from './LanguageSwitcher';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export default function SystemAdminLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdventurer } = useCurrentUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badges, setBadges] = useState({
    tribunalModeration: 0,
    partnerApplications: 0,
    guildFoundations: 0,
    newUsers24h: 0,
    tribunalActions: 0,
    tribunalModerators: 0,
    tribunalAppeals: 0,
    xpAudits: 0,
  });

  const handleLogout = async () => {
    await logoutUser();
    navigate('/', { replace: true });
  };

  useEffect(() => {
    let active = true;
    const loadBadges = async () => {
      try {
        const data = await getAdminBadges();
        if (active && data) {
          setBadges({
            tribunalModeration: data.tribunalModeration || 0,
            partnerApplications: data.partnerApplications || 0,
            guildFoundations: data.guildFoundations || 0,
            newUsers24h: data.newUsers24h || 0,
            tribunalActions: data.tribunalActions || 0,
            tribunalModerators: data.tribunalModerators || 0,
            tribunalAppeals: data.tribunalAppeals || 0,
            xpAudits: data.xpAudits || 0,
          });
        }
      } catch (err) {
        // Silently tolerate badge fetch error
      }
    };

    loadBadges();
    const interval = setInterval(loadBadges, 30000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [location.pathname]);

  const navigation = [
    {
      name: t('admin_nav.dashboard', 'Dashboard / Übersicht'),
      href: '/admin',
      exact: true,
      icon: LayoutDashboard
    },
    {
      name: t('admin_directory.users', 'Benutzer'),
      href: '/admin/users',
      icon: Users,
      badge: badges.newUsers24h > 0 ? `+${badges.newUsers24h}` : null,
      badgeType: 'amber'
    },
    {
      name: t('admin_partners.nav_title', 'Partner & Anfragen'),
      href: '/admin/partners',
      icon: Store,
      badge: badges.partnerApplications > 0 ? badges.partnerApplications : null,
      badgeType: 'cyan'
    },
    {
      name: t('admin_directory.guilds', 'Gilden'),
      href: '/admin/guilds',
      icon: Shield,
      badge: badges.guildFoundations > 0 ? badges.guildFoundations : null,
      badgeType: 'emerald'
    },
    {
      name: t('admin_nav.heroic_actions', 'Tribunal'),
      href: '/admin/heroic-actions',
      icon: Award,
      badge: badges.tribunalActions > 0 ? badges.tribunalActions : null,
      badgeType: 'rose'
    },
    {
      name: t('admin_nav.appeals', 'Einsprüche'),
      href: '/admin/appeals',
      icon: Scale,
      badge: badges.tribunalAppeals > 0 ? badges.tribunalAppeals : null,
      badgeType: 'purple'
    },
    {
      name: t('admin_nav.xp_audits', 'XP-Prüfung'),
      href: '/admin/xp-audits',
      icon: ShieldAlert,
      badge: badges.xpAudits > 0 ? badges.xpAudits : null,
      badgeType: 'amber'
    },
    {
      name: t('admin_nav.tribunal_moderators', 'Tribunal-Moderatoren'),
      href: '/admin/tribunal-moderators',
      icon: Gavel,
      badge: badges.tribunalModerators > 0 ? badges.tribunalModerators : null,
      badgeType: 'purple'
    },
    {
      name: t('admin_nav.karma', 'Karma-Verwaltung'),
      href: '/admin/karma',
      icon: Scale
    },
    {
      name: t('admin_nav.settings', 'Einstellungen'),
      href: '/admin/settings',
      icon: Sliders
    },
    {
      name: t('admin_nav.class_categories', 'Klassen & Kategorien'),
      href: '/admin/class-categories',
      icon: Sword
    },
    {
      name: t('admin_nav.events', 'Events'),
      href: '/admin/events',
      icon: Calendar
    },
    {
      name: t('admin_nav.profile', 'Mein Profil'),
      href: '/admin/profile',
      icon: User
    },
  ];

  return (
    <div className="portal-shell portal-admin flex h-screen overflow-hidden bg-zinc-950">
      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <nav className={cn(
        "portal-sidebar w-72 md:w-64 flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 md:translate-x-0 md:static md:flex h-full shadow-2xl md:shadow-none",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="portal-brand p-5 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Code className="w-6 h-6 text-rose-500" />
              <h1 className="text-lg font-semibold text-zinc-100 tracking-tight">{presentationText('systemCopy.f0a96bd6bf15')}</h1>
            </div>
            <button 
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 md:hidden"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-zinc-500">Platform Admin</p>
        </div>

        <div className="flex-1 px-3 space-y-1 mt-2 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.exact}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                cn(
                  'portal-nav-link flex items-center justify-between px-3 py-2.5 min-h-[44px] text-sm font-medium transition-colors rounded-lg group',
                  isActive
                    ? 'bg-zinc-800 text-zinc-100 border-l-2 border-rose-500 font-semibold shadow-inner'
                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 border-l-2 border-transparent'
                )
              }
            >
              <div className="flex items-center gap-3 min-w-0">
                <item.icon className="w-4 h-4 shrink-0 text-zinc-400 group-hover:text-zinc-200 transition-colors" />
                <span className="truncate">{item.name}</span>
              </div>
              {item.badge != null && (
                <span
                  className={cn(
                    'ml-2 px-2 py-0.5 text-xs font-mono font-bold rounded-full border shrink-0',
                    item.badgeType === 'rose' && 'bg-rose-500/20 text-rose-300 border-rose-500/40 animate-pulse',
                    item.badgeType === 'cyan' && 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
                    item.badgeType === 'purple' && 'bg-purple-500/20 text-purple-300 border-purple-500/40',
                    item.badgeType === 'emerald' && 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
                    item.badgeType === 'amber' && 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                  )}
                >
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>

        {/* Cross-portal: Adventurer Dashboard (only visible when also an Adventurer — always true for SystemAdmin) */}
        {isAdventurer && (
          <div className="px-3 pb-2 pt-2 border-t border-zinc-800/60">
            <button
              onClick={() => {
                navigate('/dashboard');
                setSidebarOpen(false);
              }}
              className="flex items-center justify-center gap-2 w-full px-3 py-2.5 min-h-[44px] text-xs font-bold text-amber-400 hover:text-amber-300 bg-amber-950/30 hover:bg-amber-950/50 border border-amber-800/50 rounded-lg transition-colors"
              title={t('nav_cross.tooltip_dual_role')}
            >
              <Sword className="w-4 h-4 shrink-0" />
              <span>{t('nav_cross.goto_adventurer_dashboard', 'Zum Abenteurer-Portal')}</span>
            </button>
          </div>
        )}

        <div className="p-4 mt-auto border-t border-zinc-800 space-y-3">
          <div className="flex items-center justify-between gap-2 px-1">
            <LanguageSwitcher />
            <ThemeSwitcher variant="toggle" />
          </div>

          <div className="flex items-center justify-between pt-1">
            {user && (
              <p className="text-xs text-zinc-400 truncate max-w-[140px]" title={user.username}>{user.username}</p>
            )}
            <button
              onClick={() => {
                handleLogout();
                setSidebarOpen(false);
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-rose-400 hover:text-rose-300 hover:bg-rose-950/30 rounded transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>{t('admin_nav.logout', 'Abmelden')}</span>
            </button>
          </div>
        </div>
      </nav>

      <main data-page-scroll className="portal-main flex-1 flex flex-col h-full min-w-0 overflow-y-auto">
        <header className="portal-topbar sticky top-0 z-30 h-16 shrink-0 flex items-center justify-between px-4 sm:px-6 lg:px-8 border-b border-zinc-800/80 bg-zinc-950/80 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 text-zinc-400 hover:text-zinc-200 md:hidden transition-colors rounded-lg hover:bg-zinc-800"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <span className="font-serif font-bold text-base text-zinc-100 hidden sm:inline">Admin-Konsole</span>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            <div className="hidden sm:flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeSwitcher variant="toggle" />
            </div>

            <div className="text-right">
              <p className="text-xs sm:text-sm font-medium text-zinc-200 truncate max-w-[120px] sm:max-w-none">{user?.username ?? 'System Core'}</p>
              <p className="text-[10px] sm:text-xs text-rose-400 font-mono">Root Access</p>
            </div>
          </div>
        </header>

        <div className="portal-content flex-1 p-3 sm:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
